import type { ReactNode } from 'react'
import { ThemeContext } from './ThemeContext'
import { darkTheme, lightTheme } from './themes'
import type { WllTheme } from './types'

export type ThemeProviderProps = {
	children?: ReactNode
} & ({ theme?: 'light' | 'dark'; themeOptions?: never } | { theme: 'custom'; themeOptions: WllTheme })

/**
 * Provides a theme context to its child components.
 *
 * @param {Object} params - The parameters for the ThemeProvider component.
 * @param {'light' | 'dark' | 'custom'} [params.theme='light'] - The theme to apply, defaults to 'light'.
 * @param {React.ReactNode} params.children - The child components that require access to the theme context.
 * @param {WllTheme} [params.themeOptions] - The custom theme options to use when the 'custom' theme is selected.
 * @return {JSX.Element} The ThemeProvider component that wraps its children with the selected theme context.
 */
export function ThemeProvider({ theme = 'light', children, themeOptions }: ThemeProviderProps): JSX.Element {
	const resolvedTheme = theme === 'custom' ? (themeOptions as WllTheme) : theme === 'dark' ? darkTheme : lightTheme
	const mode = theme === 'dark' ? 'dark' : 'light'

	return (
		<ThemeContext.Provider value={{ mode, theme: resolvedTheme }}>
			<div className={`${mode === 'dark' ? 'dark' : ''} font-sans`}>{children}</div>
		</ThemeContext.Provider>
	)
}
